/// <reference types="google.maps" />

import Address from "@repo/utils/Address";
import Result from "@repo/utils/Result";
import UFunction from "@repo/utils/UFunction";

import UHTMLScriptElement from "../UHTMLScriptElement.mjs";

namespace GoogleMaps {
  const callbackName = "googleMapsServiceCallback";

  export const load = UFunction.memo(
    (apiKey: string) =>
      new Promise<typeof google.maps>((resolve, reject) => {
        (window as any)[callbackName] = () => {
          delete (window as any)[callbackName];
          resolve(google.maps);
        };
        UHTMLScriptElement.inject(
          `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&libraries=places&callback=${callbackName}`
        ).catch((e) => {
          load.invalidate(apiKey);
          reject(e);
        });
      })
  );

  export type Place = google.maps.places.Place;
  export namespace Place {
    export type Id = string & {};
    export const fetch = (
      client: typeof google.maps,
      id: Id,
      {
        fields = [],
        ...options
      }: Omit<google.maps.places.PlaceOptions, "id"> & {
        fields?: string[];
      } = {}
    ) =>
      new client.places.Place({ id, ...options })
        .fetchFields({
          fields: ["addressComponents", "location", ...fields],
        })
        .then(({ place }) => place);

    export class InvalidPlaceError extends Error {
      name = "GoogleMaps.Place.InvalidPlaceError";
      message = "Invalid Google Maps place.";

      // Messages cannot by dynamically set, apps may need to redefine to
      // support translations
      constructor() {
        super();
      }
    }
    export class InvalidComponentError extends Error {
      name = "GoogleMaps.Place.MissingComponentError";

      constructor(
        public component: keyof Address,
        message: string
      ) {
        super(message);
      }
    }
    export function toAddress(place: Place) {
      return toAddress.safe(place).unwrap();
    }
    export namespace toAddress {
      export const safe = (place: Place) =>
        toAddress.partial.safe(place).map((partial) => {
          if (!partial.addressLine1)
            return Result.err(
              new InvalidComponentError("addressLine1", "Invalid street.")
            );
          if (!partial.locality)
            return Result.err(
              new InvalidComponentError("locality", "Invalid city.")
            );
          if (!partial.subdivision)
            return Result.err(
              new InvalidComponentError(
                "subdivision",
                "Invalid state/province."
              )
            );
          if (!partial.postalCode)
            return Result.err(
              new InvalidComponentError("postalCode", "Invalid postal code.")
            );
          if (!partial.country)
            return Result.err(
              new InvalidComponentError("country", "Invalid country.")
            );

          return partial as Address;
        });

      export function partial(place: Place) {
        return partial.safe(place).unwrap();
      }
      export namespace partial {
        export function safe(
          place: Place
        ): Result<Partial<Address>, InvalidPlaceError> {
          if (!place.addressComponents)
            return Result.err(new InvalidPlaceError());

          const componentByType = Object.fromEntries(
            place.addressComponents.flatMap((component) =>
              component.types.map((type) => [type, component])
            ) || []
          );

          return Result.ok({
            addressLine1:
              componentByType.route &&
              [
                componentByType.street_number?.longText,
                componentByType.route.longText,
              ]
                .filter(Boolean)
                .join(" "),
            locality: componentByType.locality?.longText || undefined,
            subdivision:
              componentByType.administrative_area_level_1?.shortText ||
              undefined,
            postalCode: componentByType.postal_code?.longText || undefined,
            country: componentByType.country?.shortText || undefined,
          });
        }
      }
    }

    export namespace Suggestion {
      export const fetch = (
        client: typeof google.maps,
        input: string,
        {
          includedPrimaryTypes = ["street_address"],
          ...options
        }: Omit<google.maps.places.AutocompleteRequest, "input"> = {}
      ) =>
        client.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          ...options,
          includedPrimaryTypes,
        }).then(({ suggestions }) => suggestions);
    }
  }
}

export default GoogleMaps;
